import type { StateSource } from "@ngrx/signals";
import { isDefined, isPropertyDefined } from "@recursyve/nice-ts-utils";
import type { MonoTypeOperatorFunction, Observable } from "rxjs";
import { combineLatest, defer, timer } from "rxjs";
import { finalize, map } from "rxjs/operators";

export type SetLoadingStateOptions = {
    minimumDuration?: number;
};

export function setLoadingState<
    T,
    State extends Record<string, unknown>,
    StateKey extends string & keyof State,
    fn extends `set${Capitalize<StateKey>}`
>(
    store: Record<fn, (value: boolean) => void> & StateSource<State>,
    state: StateKey,
    options?: SetLoadingStateOptions
): MonoTypeOperatorFunction<T> {
    const propertyKey = state[0]?.toUpperCase().concat(state.slice(1));
    const stateFnKey = `set${propertyKey}` as fn;
    const stateUpdateFn = store[stateFnKey] as (value: boolean) => void;

    return (source: Observable<T>) =>
        defer(() => {
            stateUpdateFn(true);

            if (!isDefined(options) || !isPropertyDefined(options, "minimumDuration")) {
                return source;
            }

            return combineLatest([source, timer(options.minimumDuration)]).pipe(map(([value]) => value));
        }).pipe(
            finalize(() => {
                stateUpdateFn(false);
            })
        );
}
